import { BehaviorSubject, Observable } from 'rxjs';
import { IMenuItem } from '../interfaces/MenuItem.interface';

/** Класс узла дерева */
export class MenuItem {
    /** Доступы */
    private readonly _access: { [key: string]: boolean } = {};

    /** Наименование узла */
    public readonly name: string;

    /** Тип узла */
    public readonly type: string;

    /** Тип реестра */
    public readonly show_type: number;

    /** Иконка узла */
    public readonly icon: string;

    /** Ссылка узла */
    public readonly url: string;

    /** Ссылка внутри приложения */
    public readonly isAngular: boolean = false;

    /** Родительский узел */
    public readonly parentNode: MenuItem;

    /** Id узла в дереве меню */
    public readonly id: number | string;

    /** Дата календаря для журналов */
    public readonly scheduler_date?: string;

    /** Имеет ли узел потомков */
    public expandable: boolean;

    /** Уровень, на котором находиться узел */
    public level: number;

    /** Можно ли в него добавлять */
    public add: boolean;

    /** Можно ли редактировать пункт меню */
    public edit: boolean;

    /** Можно ли его удалять */
    public del: boolean;

    /** Флаг, отвечающих за хранения состояния загрузки узла */
    private _isLoading: BehaviorSubject<boolean> = new BehaviorSubject(false);

    /** Поток состояния спинера */
    public showSpinner$: Observable<boolean> = this._isLoading.asObservable();

    /** Флаг - активный узел */
    private _isActive: boolean = false;

    /** Флаг - возможно ли перетаскивать ветвь */
    private readonly _draggable: boolean;

    /** Флаг разделитель */
    private readonly _divider: boolean;

    /** Классы узла */
    private _ngClass: { [key: string]: boolean } = {
        'menu-tree-node': true,
        'cursor-pointer': true,
        'first-level': false,
        divider: false,
    };

    /** Дочерние (вложенные) узлы */
    private _children: MenuItem[] = [];

    /** Установка дополнительных классов, в зависимости от типа */
    private _generateClassList(): void {
        this._ngClass['first-level'] = this.level === 0;
        this._ngClass.divider = this._divider;
    }

    /** Получение списка классов */
    public get ngClass(): any {
        return this._ngClass;
    }

    public setLevel(level) {
        this.level = level;
        this._generateClassList();
    }

    /** Получение состояние флага переноса узла */
    public get draggable(): boolean {
        return this._draggable;
    }

    /**
     * Установка свойств класса (инициализация)
     * @param data - данные для инициализации
     */
    constructor(data: IMenuItem) {
        this.id = data.id;
        this.parentNode = data?.parentNode;
        this.level = data?.level;
        this.name = data?.name;
        this.type = data?.type;
        this.show_type = data?.show_type ?? 0;
        this.url = data?.url ?? '';
        this.icon = data?.css_class;
        this.scheduler_date = data?.scheduler_date;
        this._draggable = data?.draggable === true;
        this.isAngular = data?.is_new_link === true;
        this._divider = data?.divider === true;
        this.access = data;
        this.isActive = data?.is_current === true;
        this.add = data.add === true;
        this.edit = data.edit === true;
        this.del = data.del === true;
        this._generateClassList();
    }

    /** Getter for _children */
    public get children(): MenuItem[] {
        return this._children;
    }

    /**
     * Установить потомков
     * @param data - массив потомков
     */
    public setChildren(data: MenuItem[]): void {
        if (data.length) {
            this._children = data;
            this.expandable = true;
        } else {
            this._children = [];
            this.expandable = false;
        }
    }

    /** Отобразить прилоадер */
    public showSpinner(): void {
        this._isLoading.next(true);
    }

    /** Скрыть прилоадер */
    public hideSpinner(): void {
        this._isLoading.next(false);
    }

    /**
     * Установка флага активности
     * @param flag - состояние активности
     */
    public set isActive(flag) {
        this._isActive = flag;
    }

    /** Является ли узел активным */
    public get isActive(): boolean {
        return this._isActive;
    }

    /** Удаление дочернего узла */
    public removeChild(id: number | string) {
        const index = this._children.findIndex((child) => child.id === id);
        if (index > -1) {
            this._children.splice(index, 1);
        }
    }

    /** Проставление доступов */
    public set access(data: any) {
        this._access.edit = data.hasOwnProperty('edit') && Number(data.edit) === 1;
        this._access.add = data.hasOwnProperty('add') && Number(data.add) === 1;
        this._access.remove = data.hasOwnProperty('del') && Number(data.del) === 1;
    }

    /** Доступ к редактированию */
    public get isEdit(): boolean {
        return this._access.edit;
    }

    /** Доступ к добавлению внутрь */
    public get isAdd(): boolean {
        return this.level > 2 ? false : this._access.add;
    }

    /** Доступ к удалению */
    public get isRemove(): boolean {
        return this._access.remove;
    }
}
